/**
 * Carbon line icons: the sprite set from the locked mockups' carbon-shell.js,
 * as React components. 24px grid, drawn at 16px (20px in the header), stroke
 * 1.6 with square caps and miter joins. The severity shapes and the info
 * circle are filled. Decorative by default (aria-hidden); pass `title` to give
 * a standalone icon an accessible name.
 *
 * Shared by both Wave 1 lanes; after W1.3 only Lane B adds icons here.
 */
import type { ReactElement, ReactNode, SVGProps } from 'react'
import clsx from 'clsx'

export type IconProps = Omit<SVGProps<SVGSVGElement>, 'ref' | 'children'> & {
  /** Rendered width and height in px. */
  size?: number
  /** Accessible name; when set the icon is role="img" instead of aria-hidden. */
  title?: string
}
export type IconComponent = ((props: IconProps) => ReactElement) & { displayName?: string }

function createIcon(displayName: string, body: ReactNode, { viewBox = '0 0 24 24', filled = false } = {}): IconComponent {
  const Icon = ({ size = 16, title, className, ...rest }: IconProps) => (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox={viewBox}
      width={size}
      height={size}
      fill={filled ? 'currentColor' : 'none'}
      stroke={filled ? undefined : 'currentColor'}
      strokeWidth={filled ? undefined : 1.6}
      strokeLinecap={filled ? undefined : 'square'}
      strokeLinejoin={filled ? undefined : 'miter'}
      focusable="false"
      className={clsx('flex-none', className)}
      {...(title ? { role: 'img', 'aria-label': title } : { 'aria-hidden': true })}
      {...rest}
    >
      {body}
    </svg>
  )
  Icon.displayName = displayName
  return Icon
}

export const IconFile = createIcon('IconFile', <><path d="M14 2H5v20h14V7z" /><path d="M14 2v5h5" /><path d="M8 13h8" /><path d="M8 17h8" /></>)
export const IconSliders = createIcon('IconSliders', <><path d="M20 7h-9" /><path d="M14 17H4" /><rect x="5" y="4" width="6" height="6" /><rect x="14" y="14" width="6" height="6" /></>)
export const IconHelp = createIcon('IconHelp', <><circle cx="12" cy="12" r="10" /><path d="M9.1 9a3 3 0 0 1 5.8 1c0 2-3 3-3 3" /><path d="M12 17v.5" /></>)
export const IconUpDown = createIcon('IconUpDown', <><path d="m7 15 5 5 5-5" /><path d="m7 9 5-5 5 5" /></>)
export const IconChevronRight = createIcon('IconChevronRight', <path d="m9 6 6 6-6 6" />)
export const IconChevronDown = createIcon('IconChevronDown', <path d="m6 9 6 6 6-6" />)
export const IconSearch = createIcon('IconSearch', <><circle cx="10.5" cy="10.5" r="7" /><path d="m21 21-5.5-5.5" /></>)
export const IconBell = createIcon('IconBell', <><path d="M6 9a6 6 0 0 1 12 0c0 6 2.5 8 2.5 8h-17S6 15 6 9" /><path d="M10 20.5h4" /></>)
export const IconSun = createIcon('IconSun', (
  <>
    <circle cx="12" cy="12" r="4" /><path d="M12 2v2" /><path d="M12 20v2" /><path d="m4.93 4.93 1.41 1.41" /><path d="m17.66 17.66 1.41 1.41" />
    <path d="M2 12h2" /><path d="M20 12h2" /><path d="m6.34 17.66-1.41 1.41" /><path d="m19.07 4.93-1.41 1.41" />
  </>
))
export const IconMoon = createIcon('IconMoon', <path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z" />)
export const IconArrowRight = createIcon('IconArrowRight', <><path d="M4 12h16" /><path d="m14 6 6 6-6 6" /></>)
export const IconArrowUp = createIcon('IconArrowUp', <><path d="M12 19V5" /><path d="m6 11 6-6 6 6" /></>)
export const IconArrowDown = createIcon('IconArrowDown', <><path d="M12 5v14" /><path d="m6 13 6 6 6-6" /></>)
export const IconSort = createIcon('IconSort', <><path d="m8 9 4-4 4 4" /><path d="m8 15 4 4 4-4" /></>)
export const IconMenu = createIcon('IconMenu', <><path d="M3 6h18" /><path d="M3 12h18" /><path d="M3 18h18" /></>)
export const IconClose = createIcon('IconClose', <><path d="M18 6 6 18" /><path d="m6 6 12 12" /></>)
export const IconCheck = createIcon('IconCheck', <path d="m4 12 5 5L20 6" />)
export const IconDownload = createIcon('IconDownload', <><path d="M12 3v12" /><path d="m6 9 6 6 6-6" /><path d="M4 21h16" /></>)
export const IconExternal = createIcon('IconExternal', <><path d="M14 4h6v6" /><path d="M10 14 20 4" /><path d="M18 14v6H4V6h6" /></>)
export const IconCopy = createIcon('IconCopy', <><rect x="8" y="8" width="13" height="13" /><path d="M16 8V3H3v13h5" /></>)
export const IconWaiver = createIcon('IconWaiver', <><path d="M14 2H5v20h14V7z" /><path d="M14 2v5h5" /><path d="m9 14 2 2 4-4" /></>)

// Shell nav icons (Lane A, hoisted at W1.6): not in the mockup sprite, drawn on the same 24px grid.
/** Admin Portal: a 2×2 grid of panels. */
export const IconGrid = createIcon('IconGrid', (
  <>
    <rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" />
    <rect x="3" y="14" width="7" height="7" /><rect x="14" y="14" width="7" height="7" />
  </>
))
/** Tenant management: a building. */
export const IconBuilding = createIcon('IconBuilding', (
  <><path d="M4 21V3h11v18" /><path d="M15 9h5v12" /><path d="M2 21h20" /><path d="M8 7h3M8 11h3M8 15h3" /></>
))
/** Admin queue: a list. */
export const IconList = createIcon('IconList', <><path d="M8 6h13M8 12h13M8 18h13" /><path d="M3 6h1M3 12h1M3 18h1" /></>)
/** Scan errors: a circle with an exclamation mark. */
export const IconWarning = createIcon('IconWarning', <><circle cx="12" cy="12" r="9" /><path d="M12 7v6" /><path d="M12 16v1" /></>)
/** Policy validation: a clipboard with a check. */
export const IconClipboardCheck = createIcon('IconClipboardCheck', (
  <><path d="M9 4H5v17h14V4h-4" /><path d="M9 2h6v4H9z" /><path d="m9 13 2 2 4-4" /></>
))
/** Ad-hoc scans: a bolt. */
export const IconBolt = createIcon('IconBolt', <path d="M13 2 4 14h7l-1 8 9-12h-7z" />)
/** Sign out: an open door with an arrow. */
export const IconSignOut = createIcon('IconSignOut', <><path d="M10 4H4v16h6" /><path d="m14 8 4 4-4 4" /><path d="M8 12h10" /></>)
/** Send feedback: a speech bubble. */
export const IconChat = createIcon('IconChat', <path d="M4 4h16v12H9l-5 4z" />)

// Lane B additions (not in the mockup sprite), drawn on the same 24px grid with square caps.
export const IconAdd = createIcon('IconAdd', <><path d="M12 5v14" /><path d="M5 12h14" /></>)
export const IconSubtract = createIcon('IconSubtract', <path d="M5 12h14" />)
/** The table filter trigger (TableFilterMenu): Carbon's funnel. */
export const IconFilter = createIcon('IconFilter', <path d="M3 4h18l-7 9v6l-4 2v-8z" />)
/** Continuous watch, the drift queue's tier marker: an eye. */
export const IconView = createIcon('IconView', <><path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>)
/** Full capture, the drift queue's tier marker: a camera. */
export const IconCamera = createIcon('IconCamera', <><path d="M3 7h4l2-3h6l2 3h4v13H3z" /><circle cx="12" cy="13" r="4" /></>)
/** OverflowMenu's trigger: three vertical 2px squares (a square-cap kebab), filled on a 16px grid. */
export const IconOverflow = createIcon(
  'IconOverflow',
  <>
    <rect x="7" y="3" width="2" height="2" />
    <rect x="7" y="7" width="2" height="2" />
    <rect x="7" y="11" width="2" height="2" />
  </>,
  { viewBox: '0 0 16 16', filled: true },
)

/** The info circle (the info InlineNotification), filled on a 20px grid. */
export const IconInfo = createIcon(
  'IconInfo',
  <path fillRule="evenodd" d="M10 1a9 9 0 1 0 0 18 9 9 0 0 0 0-18z M9.25 8.5h1.5v6h-1.5z M9.25 5h1.5v1.6h-1.5z" />,
  { viewBox: '0 0 20 20', filled: true },
)

// Severity shapes carry the level before color does: octagon, triangle, diamond, square (16px grid, filled).
export const IconSeverityCrit = createIcon(
  'IconSeverityCrit',
  <path fillRule="evenodd" d="M5 1h6l4 4v6l-4 4H5l-4-4V5z M7.25 4h1.5v5h-1.5z M7.25 10.5h1.5v1.5h-1.5z" />,
  { viewBox: '0 0 16 16', filled: true },
)
export const IconSeverityHigh = createIcon(
  'IconSeverityHigh',
  <path fillRule="evenodd" d="M8 1.2 15.2 14.6H0.8z M7.25 5.6h1.5v4.6h-1.5z M7.25 11.2h1.5v1.5h-1.5z" />,
  { viewBox: '0 0 16 16', filled: true },
)
export const IconSeverityMed = createIcon('IconSeverityMed', <path d="M8 1 15 8 8 15 1 8z" />, { viewBox: '0 0 16 16', filled: true })
export const IconSeverityLow = createIcon('IconSeverityLow', <rect x="3" y="3" width="10" height="10" />, { viewBox: '0 0 16 16', filled: true })
